import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Snackbar
} from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import { Button } from '@vadiun/react-components';
// import { transferReservationBuilder } from '../models/TransferReservation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faStar } from '@fortawesome/free-solid-svg-icons';
import {
  copyDriverMessageToClipboard,
  copyGroupToClipboard,
  formatAddress,
  formatDateTime,
  formatPassenger,
  groupReservationsForWhatsApp
} from '../utils/reserveDetails';
import { TipoServicioLabel } from '../models';

interface Props {
  open: boolean;
  reservationsData: any[] | null;
  onCancel?: () => void;
  onFinish?: () => void;
}

export const CreatedReservationsModal: React.FC<Props> = ({
  open,
  reservationsData,
  onFinish,
  onCancel
}: Props) => {
  const [reservationGroups, setReservationGroups] = useState<any[][]>([]);
  const [showCopyNotification, setShowCopyNotification] = useState({ open: false, message: '' });

  // Calcular grupos cuando cambien los datos de reservas
  useEffect(() => {
    if (reservationsData && reservationsData.length > 0) {
      const groups = groupReservationsForWhatsApp(reservationsData);
      setReservationGroups(groups);
    }
  }, [reservationsData]);

  // Función para copiar con notificación
  const handleCopyWithNotification = async (group: any[], groupIndex: number, totalGroups: number) => {
    let copyMessage = '';
    try {
      await copyGroupToClipboard(group, groupIndex, totalGroups);

      // Determinar el mensaje basado en el número de grupos
      if (totalGroups === 1) {
        copyMessage = '✅ Mensaje copiado al portapapeles';
      } else {
        copyMessage = `✅ Mensaje ${groupIndex + 1} copiado al portapapeles`;
      }

      setShowCopyNotification({ open: true, message: copyMessage });
    } catch (error) {
      console.error('Error al copiar:', error);
    }
  };

  const handleCopyDriver = async (reservation: any) => {
    try {
      await copyDriverMessageToClipboard(reservation);
      setShowCopyNotification({
        open: true,
        message: `✅ Datos para el chofer (Reserva #${reservation.id}) copiados`
      });
    } catch (error) {
      console.error('Error al copiar:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { }}
      fullWidth
      maxWidth="lg"
      disableEscapeKeyDown
      PaperProps={{
        style: { maxHeight: '100vh' }
      }}
    >
      {reservationsData?.length && (
        <>
          <DialogTitle>
            <div className="flex justify-between items-center">
              <h3>Detalle de las reservas creadas:</h3>
              <div>
                <Typography variant="body2" color="textSecondary">
                  Cliente
                </Typography>
                <Typography variant="body1">
                  {reservationsData?.[0].client.name}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">
                  Solicitante
                </Typography>
                <Typography variant="body1">
                  {reservationsData?.[0].requirements?.applicant?.name}
                </Typography>
              </div>
            </div>
          </DialogTitle>
          <DialogContent dividers>
            <div className="flex flex-col w-full flex-wrap gap-2">
              {reservationsData?.map((r) => {
                // const processedReservation =
                //   transferReservationBuilder.fromBackEnd(r);
                return (
                  <div key={r.id} className="flex flex-col items-center gap-2">
                    <div
                      className="flex justify-center items-center w-full"
                      style={{
                        borderTop: '6px solid #100078',
                        paddingTop: '10px'
                      }}
                    >
                      <Typography
                        variant="h6"
                        style={{ fontSize: '1.5rem', color: '#100078' }}
                      >
                        #{r.id}
                      </Typography>
                    </div>
                    <div className="flex w-full gap-2">
                      <div className="flex flex-col gap-6 w-auto bg-gray-200 p-2">
                        <div className="flex flex-col gap-6">
                          <div className="flex flex-col items-start gap-2 bg-white p-2 rounded-lg">
                            <Typography variant="body2" color="textSecondary">
                              Fecha de reserva
                            </Typography>
                            <Typography variant="body1">
                              {formatDateTime(r.reservationDatetime)}
                            </Typography>
                          </div>
                          <div className="flex flex-col items-start gap-2 bg-white p-2 rounded-lg">
                            <Typography variant="body2" color="textSecondary">
                              Tipo de Servicio:
                            </Typography>
                            <Chip
                              label={TipoServicioLabel[`${r.serviceType}`] || 'N/A'}
                              color="primary"
                              size="small"
                            />
                            {r.hasPriority && (
                              <FontAwesomeIcon icon={faStar} color="orange" />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 flex flex-col gap-2  ">
                        <div className="flex flex-wrap gap-2 bg-gray-200 p-2">
                          {r.locations?.map((location, index) => (
                            <div
                              key={index}
                              className="flex flex-col p-4 border rounded-lg w-fit min-w-0 flex-shrink-0 bg-white"
                            >
                              <Typography variant="subtitle1" gutterBottom>
                                Punto {index + 1}
                              </Typography>

                              <div className="flex flex-col items-start gap-2 mb-2">
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  📍 Dirección:
                                </Typography>
                                <Typography variant="body1">
                                  {formatAddress(location.address)}
                                </Typography>
                              </div>

                              {location.observation && (
                                <div className="flex items-center gap-2 mb-2">
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    📝 Observación:
                                  </Typography>
                                  <Typography variant="body1">
                                    {location.observation}
                                  </Typography>
                                </div>
                              )}

                              {location.passengers &&
                                location.passengers.length > 0 && (
                                  <div className="flex flex-col items-start gap-2 mb-2">
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                      gutterBottom
                                    >
                                      👥 Pasajeros ({location.passengers.length}
                                      ):
                                    </Typography>
                                    <div className="flex flex-col justify-center items-start gap-2">
                                      {location.passengers.map(
                                        (passenger, pIndex) => (
                                          <div
                                            key={pIndex}
                                            className="ml-4 mb-1"
                                          >
                                            <Typography variant="body2">
                                              {pIndex + 1}.{' '}
                                              {formatPassenger(passenger)}
                                            </Typography>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-around items-center col-span-6 w-full relative ">
              <div className="flex gap-2">
                {reservationGroups.length > 1 && (
                  <Typography
                    variant="body1"
                    style={{ fontSize: '16px' }}
                    className="flex items-center"
                  >
                    Copiar en {reservationGroups.length} mensajes:
                  </Typography>
                )}
                {reservationGroups.map((group, index) => (
                  <div
                    key={index}
                    onClick={() =>
                      handleCopyWithNotification(
                        group,
                        index,
                        reservationGroups.length
                      )
                    }
                    style={{
                      backgroundColor: '#100078',
                      color: '#ffffff',
                      borderRadius: '8px',
                      padding: '8px',
                      height: '36px',
                      width: 'auto',
                      minWidth: '32px',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0px 0px 8px 0.5px rgba(162, 71, 255, 0.6)',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#100078';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.border = '1px solid #100078';
                      e.currentTarget.style.boxShadow =
                        '0px 0px 8px 1px rgba(166, 0, 199, 0.9)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#100078';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow =
                        '0px 0px 8px 0.5px rgba(162, 71, 255, 0.6)';
                    }}
                  >
                    {reservationGroups.length > 1 ? (
                      <span style={{ fontSize: '16px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        mensaje {index + 1}
                      </span>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>Copiar para mensaje al cliente</span>
                        <FontAwesomeIcon
                          icon={faCopy}
                          style={{ fontSize: '16px' }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <div onClick={() => handleCopyDriver(reservationsData?.[0])}
                  style={{
                    backgroundColor: '#a247ff', 
                    color: '#ffffff',
                    borderRadius: '8px',
                    padding: '8px',
                    height: '36px',
                    width: 'auto',
                    minWidth: '32px',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0px 0px 8px 0.5px rgba(16, 0, 120, 0.4)', 
                    cursor: 'pointer',
                    border: '1px solid transparent',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#a600c7'; 
                    e.currentTarget.style.color = '#ffffff';
                    e.currentTarget.style.border = '1px solid #a600c7';
                    e.currentTarget.style.boxShadow =
                      '0px 0px 8px 1px rgba(16, 0, 120, 0.7)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#a247ff';
                    e.currentTarget.style.color = '#ffffff';
                    e.currentTarget.style.border = '1px solid transparent';
                    e.currentTarget.style.boxShadow =
                      '0px 0px 8px 0.5px rgba(16, 0, 120, 0.4)';
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>Copiar para mensaje al chofer</span>
                    <FontAwesomeIcon
                      icon={faCopy}
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                </div>
              </div>
              <div className="absolute top-0 right-0">
                {onFinish && (
                  <Button variant="contained" color="blue" onClick={onFinish}>
                    Aceptar
                  </Button>
                )}
                {onCancel && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={onCancel}
                  >
                    Cerrar
                  </Button>
                )}
              </div>
            </div>
          </DialogActions>
        </>
      )}

      <Snackbar
        open={showCopyNotification.open}
        autoHideDuration={2000}
        onClose={() => setShowCopyNotification({ open: false, message: '' })}
        message={showCopyNotification.message}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Dialog>
  );
};
